/**
 * GameContext — Phase 38 canvas foundation + Phase 39 exit handler registration.
 *
 * Holds ONLY scalar values (tier, isActive, activate, deactivate, exitGame).
 * Mutable GameState (mouse coords, enemies, etc.) lives in a useRef inside
 * GameCanvas.tsx — NEVER in this context. See 38-CONTEXT.md locked decisions.
 *
 * Mirrors src/context/UIEffectsContext.tsx pattern:
 *  - createContext<T | null>(null)
 *  - Provider owns useState, callbacks wrapped in useCallback
 *  - Context value wrapped in useMemo so consumers (Navbar, Footer, Home)
 *    do not re-render when tier/isActive haven't changed
 *  - Consumer hook throws when called outside Provider
 *
 * Phase 39 additions:
 *  - exitGame(): invokes the exit handler registered by GameCanvas on mount.
 *    This allows the DOM [stop game] button in Home.tsx to call the identical
 *    teardown as the in-game Escape path.
 *  - registerExitHandler(): GameCanvas registers its internal exitGame closure
 *    on activation and clears it on deactivation (prevents stale closure calls).
 *
 * SAFE-03: activate() guards against touch devices via matchMedia.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { GameTier } from './types';

const TOUCH_MEDIA_QUERY = '(hover: none) and (pointer: coarse)';

export interface GameContextValue {
  tier: GameTier;
  isActive: boolean;
  activate: () => void;
  deactivate: () => void;
  exitGame: () => void;                                   // Phase 39 — shared exit entrypoint
  registerExitHandler: (handler: (() => void) | null) => void; // GameCanvas registers/unregisters its impl
  setTier: (tier: GameTier) => void;  // Phase 42 — wave-threshold tier progression from GameCanvas update loop
}

const GameContext = createContext<GameContextValue | null>(null);

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tier, setTier] = useState<GameTier>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const exitHandlerRef = useRef<(() => void) | null>(null);

  const activate = useCallback((): void => {
    // SAFE-03: hard touch-device guard — no game on hover:none + pointer:coarse
    if (window.matchMedia(TOUCH_MEDIA_QUERY).matches) {
      return;
    }
    setIsActive(true);
    setTier(1);
  }, []);

  const deactivate = useCallback((): void => {
    setIsActive(false);
    setTier(0);
  }, []);

  const exitGame = useCallback((): void => {
    exitHandlerRef.current?.();
  }, []);

  const registerExitHandler = useCallback((handler: (() => void) | null): void => {
    exitHandlerRef.current = handler;
  }, []);

  // useMemo prevents cascading re-renders of portfolio components on tier changes.
  // Without this, every consumer (Navbar, Footer, Home) re-renders whenever tier or isActive updates.
  const value = useMemo<GameContextValue>(
    () => ({ tier, isActive, activate, deactivate, exitGame, registerExitHandler, setTier }),
    [tier, isActive, activate, deactivate, exitGame, registerExitHandler, setTier]
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

export const useGameContext = (): GameContextValue => {
  const ctx = useContext(GameContext);
  if (!ctx) {
    throw new Error('useGameContext must be called inside GameProvider');
  }
  return ctx;
};
